import { useTranslation } from "react-i18next";
import { Check, Sparkles } from "lucide-react";

export default function PricingPreview({ onBasicCta, onProCta }) {
  const { t } = useTranslation();
  const basicFeatures = t("pricingPreview.basic.features", { returnObjects: true });
  const proFeatures = t("pricingPreview.pro.features", { returnObjects: true });

  return (
    <section
      id="pricing"
      className="bg-white border-b border-slate-200 scroll-mt-4 sm:scroll-mt-8"
      data-testid="section-pricing"
    >
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
        <div className="max-w-2xl">
          <p className="text-[11px] tracking-[0.2em] uppercase font-semibold text-amber-700">
            {t("pricingPreview.eyebrow")}
          </p>
          <h2 className="mt-3 font-display font-bold text-slate-900 tracking-tight text-3xl sm:text-4xl leading-[1.1]">
            {t("pricingPreview.title")}
          </h2>
          <p className="mt-4 text-base text-slate-600 leading-relaxed">
            {t("pricingPreview.subtitle")}
          </p>
        </div>

        <div className="mt-12 grid grid-cols-1 md:grid-cols-2 gap-5 sm:gap-6 max-w-4xl">
          {/* Basic */}
          <article className="border-2 border-amber-500 rounded-xl bg-white p-6 sm:p-8 flex flex-col relative" data-testid="pricing-basic">
            <div className="absolute -top-3 left-6 inline-flex items-center px-2.5 py-1 rounded-full bg-amber-500 text-white text-[10px] font-semibold tracking-wider uppercase">
              {t("pricingPreview.basic.tag")}
            </div>
            <h3 className="mt-2 font-display text-xl font-bold text-slate-900">
              {t("pricingPreview.basic.name")}
            </h3>
            <div className="mt-4 flex items-baseline gap-2 flex-wrap">
              <span className="font-display text-4xl font-bold text-slate-900 tabular-nums">
                {t("pricingPreview.basic.price")}
              </span>
              <span className="text-[12px] text-slate-500">{t("pricingPreview.basic.priceNote")}</span>
            </div>
            <ul className="mt-6 space-y-2.5 flex-1">
              {Array.isArray(basicFeatures) && basicFeatures.map((f, i) => (
                <li key={i} className="flex items-start gap-2 text-[14px] text-slate-700">
                  <Check className="w-4 h-4 text-emerald-600 mt-0.5 flex-shrink-0" />
                  <span>{f}</span>
                </li>
              ))}
            </ul>
            <button
              type="button"
              onClick={onBasicCta}
              className="mt-7 h-11 w-full rounded-md bg-slate-900 text-white text-sm font-semibold hover:bg-slate-800 transition-colors"
              data-testid="pricing-basic-cta"
            >
              {t("pricingPreview.basic.cta")}
            </button>
          </article>

          {/* Dhruva Pro */}
          <article className="border border-slate-700 rounded-xl bg-slate-900 text-white p-6 sm:p-8 flex flex-col relative" data-testid="pricing-pro">
            <div className="absolute -top-3 left-6 inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-amber-400 text-slate-900 text-[10px] font-semibold tracking-wider uppercase">
              <Sparkles className="w-3 h-3" />
              {t("pricingPreview.pro.tag")}
            </div>
            <h3 className="mt-2 font-display text-xl font-bold text-white">
              {t("pricingPreview.pro.name")}
            </h3>
            <div className="mt-4 flex items-baseline gap-2 flex-wrap">
              <span className="font-display text-4xl font-bold text-white tabular-nums">
                {t("pricingPreview.pro.price")}
              </span>
              <span className="text-[12px] text-slate-300">{t("pricingPreview.pro.priceNote")}</span>
            </div>
            <ul className="mt-6 space-y-2.5 flex-1">
              {Array.isArray(proFeatures) && proFeatures.map((f, i) => (
                <li key={i} className="flex items-start gap-2 text-[14px] text-slate-200">
                  <Check className="w-4 h-4 text-amber-400 mt-0.5 flex-shrink-0" />
                  <span>{f}</span>
                </li>
              ))}
            </ul>
            <button
              type="button"
              onClick={onProCta}
              className="mt-7 h-11 w-full rounded-md bg-amber-500 text-slate-900 text-sm font-semibold hover:bg-amber-400 transition-colors"
              data-testid="pricing-pro-cta"
            >
              {t("pricingPreview.pro.cta")}
            </button>
            <button
              type="button"
              onClick={onBasicCta}
              className="mt-3 text-[12px] text-amber-300 hover:text-amber-200 underline decoration-amber-400/40 underline-offset-4 transition-colors text-center w-full"
              data-testid="pricing-pro-buy-basic-now"
            >
              {t("pricingPreview.pro.buyBasicNow")}
            </button>
            <p className="mt-3 text-[11px] text-slate-400 text-center" data-testid="pricing-pro-note">
              {t("pricingPreview.pro.comingSoon")}
            </p>
          </article>
        </div>

        <p className="mt-8 text-[12px] text-slate-500" data-testid="pricing-trust">
          {t("pricingPreview.trust")}
        </p>
      </div>
    </section>
  );
}
